<template>
  <div class="panel-overlay" :class="{ open: visible }" @click.self="$emit('close')">
    <div class="panel-card content-page-panel" :class="{ 'feedback-mode': isHelp }">
      <div class="panel-header">
        <span>{{ title }}</span>
        <button @click="$emit('close')">×</button>
      </div>
      <div class="panel-body content-body">
        <div v-if="loading" class="content-loading">加载中…</div>

        <!-- 帮助与反馈：显示帮助内容 + 反馈表单 -->
        <template v-else-if="isHelp">
          <div v-if="content" class="content-html" v-html="content"></div>
          <div v-else class="content-empty">
            <p>暂无帮助内容</p>
          </div>

          <div class="feedback-divider">
            <span>提交反馈</span>
          </div>

          <div v-if="feedbackSubmitted" class="feedback-success">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <p>感谢您的反馈，我们会尽快处理！</p>
          </div>

          <form v-else class="feedback-form" @submit.prevent="submitFeedback">
            <div class="ff-row">
              <div class="ff-group">
                <label>联系电话 <span class="optional">(选填)</span></label>
                <input v-model="fb.contact_phone" type="tel" placeholder="方便我们联系您" maxlength="30" />
              </div>
              <div class="ff-group">
                <label>联系邮箱 <span class="optional">(选填)</span></label>
                <input v-model="fb.contact_email" type="email" placeholder="example@email.com" maxlength="200" />
              </div>
            </div>

            <div class="ff-group">
              <label>反馈类型</label>
              <div class="ff-types">
                <button
                  v-for="opt in feedbackTypes"
                  :key="opt.value"
                  type="button"
                  class="ff-type-btn"
                  :class="{ active: fb.feedback_type === opt.value }"
                  @click="fb.feedback_type = opt.value"
                >{{ opt.label }}</button>
              </div>
            </div>

            <div class="ff-group">
              <label>反馈标题 <span class="optional">(选填)</span></label>
              <input v-model="fb.title" placeholder="简要描述您的问题" maxlength="200" />
            </div>

            <div class="ff-group">
              <label>问题描述 <span class="required">*</span></label>
              <textarea
                v-model="fb.description"
                rows="4"
                placeholder="请详细描述您遇到的问题或建议…"
                maxlength="5000"
              ></textarea>
              <div class="ff-chars">{{ fb.description.length }}/5000</div>
            </div>

            <button type="submit" class="ff-submit" :disabled="submitting || !fb.description.trim()">
              {{ submitting ? '提交中…' : '提交反馈' }}
            </button>
            <p v-if="fbError" class="ff-error">{{ fbError }}</p>
          </form>
        </template>

        <!-- 用户协议/隐私政策：仅显示内容 -->
        <template v-else>
          <div v-if="content" class="content-html" v-html="content"></div>
          <div v-else class="content-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.3">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
            <p>暂无内容</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive } from 'vue'
import { usePortal } from '@/stores/portal'

const props = defineProps<{ visible: boolean; type: 'terms' | 'privacy' | 'help' }>()
const emit = defineEmits<{ close: [] }>()

const portal = usePortal()
const loading = ref(false)
const content = ref('')
const isHelp = computed(() => props.type === 'help')

const titles: Record<string, string> = {
  terms: '用户协议',
  privacy: '隐私政策',
  help: '帮助与反馈',
}
const title = computed(() => titles[props.type] || '')

async function loadContent() {
  loading.value = true
  content.value = ''
  resetFeedback()
  try {
    const res = await portal.apiGet('site_config.php?all=1')
    const cfg = res.data || {}
    const key = props.type + '_content'
    content.value = cfg[key] || getDefaultContent(props.type)
  } catch (_) {
    content.value = getDefaultContent(props.type)
  }
  loading.value = false
}

watch(() => [props.visible, props.type], async ([v]) => {
  if (v) await loadContent()
})

function getDefaultContent(type: string): string {
  switch (type) {
    case 'terms':
      return `<h2>用户协议</h2>
<p>欢迎使用 AI Gateway 智能对话平台。</p>
<h3>1. 服务说明</h3>
<p>本平台提供基于人工智能技术的对话服务，包括但不限于智能问答、内容创作、图片生成、视频生成等功能。</p>
<h3>2. 用户责任</h3>
<p>用户不得利用本平台从事任何违法违规活动，包括但不限于：生成违法内容、侵犯他人权益、传播虚假信息等。</p>
<h3>3. 内容免责</h3>
<p>AI生成内容仅供参考，不可作为法律证据或专业意见。用户应自行甄别信息准确性，平台不对AI生成内容承担法律责任。</p>
<h3>4. 服务变更</h3>
<p>平台保留随时修改或中断服务的权利，无需事先通知用户。</p>
<h3>5. 协议更新</h3>
<p>本协议可能不定期更新，继续使用即视为同意最新版本。</p>
<p style="margin-top:24px;color:var(--text-tertiary);font-size:13px">最后更新：2026年6月</p>`

    case 'privacy':
      return `<h2>隐私政策</h2>
<p>我们重视您的隐私。本政策说明我们如何收集、使用和保护您的个人信息。</p>
<h3>1. 信息收集</h3>
<p>我们收集的信息包括：您主动提供的对话内容、账号信息（用户名、头像）、以及使用过程中产生的日志数据。</p>
<h3>2. 信息使用</h3>
<p>收集的信息用于：提供和改进服务、保障账号安全、遵守法律法规要求。</p>
<h3>3. 信息存储</h3>
<p>对话记录和用户数据存储在我们的服务器上，采用加密传输和存储保护措施。</p>
<h3>4. 信息共享</h3>
<p>未经您同意，我们不会向第三方共享您的个人信息，法律要求除外。</p>
<h3>5. 您的权利</h3>
<p>您有权访问、更正、删除您的个人信息。如需行使这些权利，请联系管理员。</p>
<p style="margin-top:24px;color:var(--text-tertiary);font-size:13px">最后更新：2026年6月</p>`

    case 'help':
      return `<h2>帮助中心</h2>
<h3>快速入门</h3>
<p>在底部输入框输入您的问题，选择角色后发送即可开始对话。平台支持多种模式：</p>
<ul>
  <li><strong>对话模式</strong>：与AI进行智能问答对话</li>
  <li><strong>AI创作</strong>：文生图、图生图、文生视频、图生视频</li>
  <li><strong>深度思辨</strong>：多个AI角色从不同角度辩论分析</li>
  <li><strong>禅模式</strong>：冥想引导和心灵对话</li>
</ul>
<h3>常见问题</h3>
<p><strong>Q: 如何切换角色？</strong><br>在输入框上方的角色选择器中切换，不同角色有不同的专长领域。</p>
<p><strong>Q: 如何分享对话？</strong><br>点击顶部工具栏的分享按钮，或点击单条消息旁的分享按钮。</p>
<p><strong>Q: 如何导出对话？</strong><br>在设置面板中选择导出格式（HTML/Markdown/JSON/TXT）。</p>`

    default:
      return ''
  }
}

// ========== 反馈表单 ==========
const feedbackTypes = [
  { label: '功能故障', value: 'bug' },
  { label: '功能建议', value: 'feature' },
  { label: '使用问题', value: 'usage' },
  { label: '账号问题', value: 'account' },
  { label: '其他', value: 'other' },
]

const fb = reactive({
  contact_phone: '',
  contact_email: '',
  feedback_type: 'bug',
  title: '',
  description: '',
})
const submitting = ref(false)
const feedbackSubmitted = ref(false)
const fbError = ref('')

function resetFeedback() {
  fb.contact_phone = ''
  fb.contact_email = ''
  fb.feedback_type = 'bug'
  fb.title = ''
  fb.description = ''
  submitting.value = false
  feedbackSubmitted.value = false
  fbError.value = ''
}

async function submitFeedback() {
  if (!fb.description.trim()) {
    fbError.value = '请填写问题描述'
    return
  }
  if (fb.description.length > 5000) {
    fbError.value = '问题描述不能超过5000字'
    return
  }
  fbError.value = ''
  submitting.value = true
  try {
    const token = portal.getToken()
    const resp = await fetch('/api/feedback.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
      },
      body: JSON.stringify({ ...fb }),
    })
    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}))
      throw new Error(errData.error || '服务器错误')
    }
    feedbackSubmitted.value = true
  } catch (e: any) {
    fbError.value = e.message || '提交失败，请稍后重试'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.panel-overlay {
  position: fixed; inset: 0; z-index: 400;
  background: rgba(0,0,0,0.3);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; visibility: hidden; transition: all 0.25s;
}
.panel-overlay.open { opacity: 1; visibility: visible; }
.panel-card {
  background: var(--bg-secondary); border: 1px solid var(--border-color);
  border-radius: 16px; box-shadow: var(--shadow);
  width: 560px; max-width: 92vw; max-height: 80vh;
  display: flex; flex-direction: column;
}
.panel-card.feedback-mode { width: 600px; }
.panel-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid var(--border-color);
  font-size: 15px; font-weight: 600;
}
.panel-header button {
  background: none; border: none; font-size: 22px; color: var(--text-tertiary);
  cursor: pointer; line-height: 1; padding: 0 4px;
}
.panel-header button:hover { color: var(--text-primary); }
.panel-body { padding: 20px 24px; overflow-y: auto; flex: 1; }

.content-loading, .content-empty { text-align: center; padding: 40px 0; color: var(--text-tertiary); }
.content-empty p { margin-top: 12px; font-size: 14px; }

.content-html { font-size: 14px; line-height: 1.7; color: var(--text-primary); }
.content-html :deep(h2) { font-size: 18px; margin: 0 0 16px; }
.content-html :deep(h3) { font-size: 15px; margin: 20px 0 8px; }
.content-html :deep(p) { margin: 8px 0; }
.content-html :deep(ul), .content-html :deep(ol) { padding-left: 20px; margin: 8px 0; }
.content-html :deep(li) { margin-bottom: 4px; }
.content-html :deep(strong) { font-weight: 600; }

/* ========== 反馈表单 ========== */
.feedback-divider {
  display: flex; align-items: center; gap: 12px;
  margin: 24px 0 16px;
  color: var(--text-tertiary); font-size: 13px;
}
.feedback-divider::before, .feedback-divider::after {
  content: ''; flex: 1; height: 1px;
  background: var(--border-color);
}

.feedback-form { display: flex; flex-direction: column; gap: 14px; }
.ff-row { display: flex; gap: 12px; }
.ff-group { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.ff-group label { font-size: 13px; font-weight: 500; color: var(--text-secondary); }
.ff-group .optional { font-weight: 400; color: var(--text-tertiary); font-size: 11px; }
.ff-group .required { color: #e74c3c; }
.ff-group input, .ff-group textarea {
  padding: 8px 12px; border: 1px solid var(--border-color);
  border-radius: 8px; font-size: 13px; background: var(--bg-primary);
  color: var(--text-primary); outline: none; transition: border-color 0.2s;
  font-family: inherit; resize: vertical;
}
.ff-group input:focus, .ff-group textarea:focus { border-color: var(--accent); }
.ff-group textarea { min-height: 80px; }
.ff-chars { text-align: right; font-size: 11px; color: var(--text-tertiary); }

.ff-types { display: flex; gap: 6px; flex-wrap: wrap; }
.ff-type-btn {
  padding: 5px 12px; border: 1px solid var(--border-color);
  border-radius: 16px; font-size: 12px; cursor: pointer;
  background: var(--bg-primary); color: var(--text-secondary);
  transition: all 0.2s;
}
.ff-type-btn:hover { border-color: var(--accent); color: var(--accent); }
.ff-type-btn.active {
  background: var(--accent); color: #fff; border-color: var(--accent);
}

.ff-submit {
  padding: 10px 0; border: none; border-radius: 8px;
  background: var(--accent); color: #fff; font-size: 14px;
  font-weight: 500; cursor: pointer; transition: opacity 0.2s;
}
.ff-submit:hover:not(:disabled) { opacity: 0.85; }
.ff-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.ff-error { color: #e74c3c; font-size: 12px; margin: 0; }

.feedback-success {
  text-align: center; padding: 24px 0;
  color: var(--accent);
}
.feedback-success p { margin-top: 8px; font-size: 14px; }
</style>
